import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, ChevronRight, ChevronDown } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import { useCompassGraph } from '../../../../hooks/useCompassGraph';
import { useDiagramStore } from '../../../../stores/diagramStore';
import { getCommandManager } from '../../../../hooks/useCommandManager';
import { CreateNetworkResourceCommand } from '../../../network/commands/CreateNetworkResourceCommand';
import type { PersistedNode } from '../../models/KnowledgeGraph';

/**
 * CompassFolderResourcesSection — tree browser over the folder's
 * knowledge graph.
 *
 * Natural AWS hierarchy:
 *   Account → VPC → Subnet → Resource
 *
 * Resources that don't belong to a VPC (IAM, S3 buckets, KMS keys,
 * CloudFront distributions, route53 zones, CloudTrail trails, etc.)
 * surface under an "Account-wide" sibling under each account. When
 * the scan lacks explicit account nodes we fall back to a single
 * "Account" root so the tree is never empty-layered.
 *
 * Click a leaf to insert a network shape on the canvas (same command
 * pipeline as the flat list).
 */

interface TreeNode {
  readonly id: string;
  readonly label: string;
  readonly kind: 'account' | 'account-wide' | 'vpc' | 'subnet' | 'type-group' | 'resource';
  readonly nodeRef?: PersistedNode;
  readonly children: TreeNode[];
  readonly descendantResourceCount: number;
}

/**
 * Drag payload format. Canvas drop handler reads this from
 * `dataTransfer` under the custom MIME type below and batch-inserts
 * the full subtree:
 *   - A wrapper frame named after the branch.
 *   - Nested frames for each container (VPC / subnet) encountered.
 *   - Individual shapes for each resource, laid out grid-in-frame.
 *   - Flow edges between resources (only edges whose both endpoints
 *     are in the dropped set survive; cross-subtree edges are dropped).
 */
export interface CompassSubtreeDragPayload {
  readonly sourceFolderId: string;
  readonly branchLabel: string;
  readonly branchKind: TreeNode['kind'];
  readonly resources: {
    readonly id: string;
    readonly manifestId: string;
    readonly label: string;
    readonly props: Record<string, unknown>;
    /** Nearest container from the source graph (subnet or VPC). */
    readonly containerId?: string;
  }[];
  readonly containers: {
    readonly id: string;
    readonly manifestId: string;
    readonly kind: 'vpc' | 'subnet' | 'account' | 'other';
    readonly label: string;
    /** Parent container id within the payload, if any. */
    readonly parentId?: string;
  }[];
  readonly edges: {
    readonly id: string;
    readonly from: string;
    readonly to: string;
    readonly type: string;
  }[];
}

export const COMPASS_SUBTREE_MIME = 'application/x-compass-subtree';

export const CompassFolderResourcesSection: React.FC<{
  folderId: string | undefined;
  orgId: string | undefined;
}> = ({ folderId, orgId }) => {
  const { t } = useTranslation('diagram');
  const { data: graph, isLoading } = useCompassGraph(folderId, orgId);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const stage = useDiagramStore((s) => s.stage);
  const canvasNodes = useDiagramStore((s) => s.nodes);
  const boundIds = useMemo(() => {
    const set = new Set<string>();
    for (const n of canvasNodes) {
      const attrs = (n as { attrs?: Record<string, unknown> }).attrs ?? {};
      const ref = attrs.compassGraphNodeId;
      if (typeof ref === 'string') set.add(ref);
    }
    return set;
  }, [canvasNodes]);

  const tree = useMemo<TreeNode[]>(() => {
    if (!graph) return [];
    return buildTree(graph.nodes, graph.edges, t);
  }, [graph, t]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo<TreeNode[]>(() => {
    if (!q) return tree;
    return filterTree(tree, q);
  }, [tree, q]);

  // When a search is active, auto-expand every branch in the filtered
  // tree so matches are visible without manual clicking.
  useEffect(() => {
    if (!q) return;
    const toOpen = new Set<string>();
    const walk = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        if (n.children.length > 0) {
          toOpen.add(n.id);
          walk(n.children);
        }
      }
    };
    walk(filtered);
    setExpanded(toOpen);
  }, [q, filtered]);

  const insert = (node: PersistedNode): void => {
    if (!folderId) return;
    const vpWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const vpHeight = typeof window !== 'undefined' ? window.innerHeight : 720;
    const scale = stage.scale || 1;
    const x = (vpWidth / 2 - stage.offsetX) / scale;
    const y = (vpHeight / 2 - stage.offsetY) / scale;
    const manifestId =
      ((node.props as Record<string, unknown> | undefined)?._manifestId as string | undefined) ??
      node.type;
    const props = (node.props ?? {}) as Record<string, unknown>;
    getCommandManager().execute(
      new CreateNetworkResourceCommand({
        type: manifestId,
        x,
        y,
        text: String(node.label ?? node.id),
        attrs: {
          ...props,
          name: String(node.label ?? node.id),
          compassGraphNodeId: node.id,
          sourceFolderId: folderId,
        },
      }),
    );
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!folderId) {
    return (
      <p className="text-[11px] text-muted-foreground">
        {t('compass.panel.resources.openFromFolder')}
      </p>
    );
  }

  if (isLoading) {
    return <p className="text-[11px] text-muted-foreground">{t('compass.panel.resources.loadingGraph')}</p>;
  }

  if (!graph || graph.nodes.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        {t('compass.panel.resources.noScanned')}
      </p>
    );
  }

  const matchCount = countResources(filtered);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('compass.panel.resources.searchPlaceholder', { count: graph.nodes.length })}
          className="h-7 pl-7 text-xs"
        />
      </div>

      <div className="text-[11px] text-muted-foreground">
        {q
          ? t('compass.panel.resources.matchCount', { count: matchCount })
          : t('compass.panel.resources.resourcesAccounts', { count: tree.length, resources: graph.nodes.length })}
      </div>

      <ScrollArea className="max-h-[480px] rounded-md border border-border/40">
        <div className="flex flex-col py-1">
          {filtered.map((node) => (
            <TreeRow
              key={node.id}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={toggle}
              onInsert={insert}
              boundIds={boundIds}
              folderId={folderId}
              graphNodes={graph.nodes}
              graphEdges={graph.edges}
            />
          ))}
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-[11px] text-muted-foreground">
              {t('compass.panel.resources.noMatchesFor', { value: query })}
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
};

// ─── Tree row ──────────────────────────────────────────────────────

const TreeRow: React.FC<{
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onInsert: (n: PersistedNode) => void;
  boundIds: Set<string>;
  folderId: string;
  graphNodes: readonly PersistedNode[];
  graphEdges: readonly GraphEdgeLike[];
}> = ({
  node,
  depth,
  expanded,
  onToggle,
  onInsert,
  boundIds,
  folderId,
  graphNodes,
  graphEdges,
}) => {
  const { t } = useTranslation('diagram');
  const isBranch = node.children.length > 0;
  const isOpen = isBranch && expanded.has(node.id);
  const indent = { paddingLeft: `${depth * 12 + 6}px` };

  const handleDragStart = (e: React.DragEvent) => {
    const resources = collectResources(node);
    if (resources.length === 0) {
      e.preventDefault();
      return;
    }
    const payload = enrichPayload(
      folderId,
      node.label,
      node.kind,
      resources,
      graphNodes,
      graphEdges,
    );
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData(COMPASS_SUBTREE_MIME, JSON.stringify(payload));
    e.dataTransfer.setData('text/plain', node.label);
  };

  if (node.kind === 'resource' && node.nodeRef) {
    const ref = node.nodeRef;
    const onCanvas = boundIds.has(ref.id);
    return (
      <button
        type="button"
        onClick={() => !onCanvas && onInsert(ref)}
        disabled={onCanvas}
        draggable={!onCanvas}
        onDragStart={handleDragStart}
        style={indent}
        className={`flex items-center justify-between gap-2 pr-2 py-1 text-left text-[11px] transition-colors ${
          onCanvas
            ? 'cursor-not-allowed text-muted-foreground/60'
            : 'hover:bg-muted text-foreground cursor-grab active:cursor-grabbing'
        }`}
        aria-label={onCanvas ? t('compass.panel.resources.alreadyOnCanvas', { value: node.label }) : t('compass.panel.resources.insertOrDrag', { value: node.label })}
      >
        <span className="min-w-0 truncate font-medium">{node.label}</span>
        <span className="shrink-0 text-muted-foreground">
          {onCanvas ? t('compass.panel.resources.onCanvas') : <Plus className="h-3 w-3" />}
        </span>
      </button>
    );
  }

  return (
    <>
      <div
        style={indent}
        draggable={node.descendantResourceCount > 0}
        onDragStart={handleDragStart}
        className="flex w-full items-center gap-1.5 pr-2 py-1 text-[11px] text-foreground hover:bg-muted cursor-grab active:cursor-grabbing"
      >
        <button
          type="button"
          onClick={() => isBranch && onToggle(node.id)}
          aria-expanded={isBranch ? isOpen : undefined}
          aria-label={isBranch ? (isOpen ? t('compass.panel.resources.collapse') : t('compass.panel.resources.expand')) : undefined}
          className="-m-0.5 rounded p-0.5 hover:bg-background/60"
        >
          {isBranch ? (
            isOpen ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )
          ) : (
            <span className="inline-block w-3" />
          )}
        </button>
        <span className={`min-w-0 flex-1 truncate ${branchEmphasis(node.kind)}`}>
          {node.label}
        </span>
        <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
          {node.descendantResourceCount}
        </span>
      </div>
      {isBranch && isOpen
        ? node.children.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onInsert={onInsert}
              boundIds={boundIds}
              folderId={folderId}
              graphNodes={graphNodes}
              graphEdges={graphEdges}
            />
          ))
        : null}
    </>
  );
};

function collectResources(
  node: TreeNode,
): CompassSubtreeDragPayload['resources'] {
  const out: CompassSubtreeDragPayload['resources'][number][] = [];
  const walk = (n: TreeNode) => {
    if (n.kind === 'resource' && n.nodeRef) {
      const ref = n.nodeRef;
      const props = (ref.props ?? {}) as Record<string, unknown>;
      const manifestId = (props._manifestId as string | undefined) ?? ref.type;
      out.push({
        id: ref.id,
        manifestId,
        label: String(ref.label ?? ref.id),
        props,
      });
      return;
    }
    for (const child of n.children) walk(child);
  };
  walk(node);
  return out;
}

/**
 * Walk graph containment + edges to enrich the drag payload so the
 * drop target can render nested frames and wire connectors between
 * dropped resources in one gesture.
 */
function enrichPayload(
  folderId: string,
  branchLabel: string,
  branchKind: TreeNode['kind'],
  rawResources: CompassSubtreeDragPayload['resources'],
  graphNodes: readonly PersistedNode[],
  graphEdges: readonly GraphEdgeLike[],
): CompassSubtreeDragPayload {
  const byId = new Map(graphNodes.map((n) => [n.id, n]));
  const resourceIdSet = new Set(rawResources.map((r) => r.id));

  // Parent lookup: normalise containment edges so parent→child is
  // uniform. Matches the logic in `buildTree`.
  const parentById = new Map<string, string>();
  for (const e of graphEdges) {
    if (!CONTAINMENT_EDGE_TYPES.has(e.type)) continue;
    if (e.type === 'in-vpc' || e.type === 'in-subnet') {
      parentById.set(e.from, e.to);
    } else {
      parentById.set(e.to, e.from);
    }
  }

  // Classify a graph node into the payload container kinds.
  const classify = (n: PersistedNode): 'vpc' | 'subnet' | 'account' | 'other' | null => {
    const m = ((n.props as Record<string, unknown> | undefined)?._manifestId as string | undefined) ?? n.type;
    if (!m) return null;
    const lm = m.toLowerCase();
    if (lm.includes('account')) return 'account';
    if (lm.includes('subnet')) return 'subnet';
    if (lm.includes('vpc') && !lm.includes('endpoint') && !lm.includes('peering')) return 'vpc';
    return null;
  };

  // Walk up from a resource collecting container ancestors we want to
  // render as frames. Stops at the first account / top-of-chain.
  const containersToEmit = new Map<string, CompassSubtreeDragPayload['containers'][number]>();
  const resourceContainerMap = new Map<string, string>();

  for (const res of rawResources) {
    let cur: string | undefined = parentById.get(res.id);
    let nearest: string | undefined;
    while (cur) {
      const n = byId.get(cur);
      if (!n) break;
      const kind = classify(n);
      if (kind && kind !== 'other') {
        const m = ((n.props as Record<string, unknown> | undefined)?._manifestId as string | undefined) ?? n.type;
        if (!nearest) nearest = cur;
        if (!containersToEmit.has(cur)) {
          containersToEmit.set(cur, {
            id: cur,
            manifestId: m ?? 'aws_container',
            kind,
            label: String(n.label ?? cur),
          });
        }
      }
      cur = parentById.get(cur);
    }
    if (nearest) resourceContainerMap.set(res.id, nearest);
  }

  // Wire parent links between emitted containers.
  const containers = Array.from(containersToEmit.values()).map((c) => {
    const parent = parentById.get(c.id);
    if (parent && containersToEmit.has(parent)) {
      return { ...c, parentId: parent };
    }
    return c;
  });

  // Attach containerId to resources.
  const resources = rawResources.map((r) => ({
    ...r,
    containerId: resourceContainerMap.get(r.id),
  }));

  // Flow edges (both endpoints in the dropped resource set, non-containment).
  const edges: CompassSubtreeDragPayload['edges'] = [];
  for (const e of graphEdges) {
    if (CONTAINMENT_EDGE_TYPES.has(e.type)) continue;
    if (!resourceIdSet.has(e.from) || !resourceIdSet.has(e.to)) continue;
    edges.push({
      id: `${e.from}__${e.to}__${e.type}`,
      from: e.from,
      to: e.to,
      type: e.type,
    });
  }

  return {
    sourceFolderId: folderId,
    branchLabel,
    branchKind,
    resources,
    containers,
    edges,
  };
}

function branchEmphasis(kind: TreeNode['kind']): string {
  switch (kind) {
    case 'account':
      return 'font-semibold uppercase tracking-wider text-[10px] text-foreground';
    case 'account-wide':
    case 'type-group':
      return 'font-medium text-muted-foreground';
    case 'vpc':
      return 'font-semibold';
    case 'subnet':
      return 'font-medium';
    default:
      return '';
  }
}

// ─── Tree construction (pure) ──────────────────────────────────────

interface GraphEdgeLike {
  readonly type: string;
  readonly from: string;
  readonly to: string;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const CONTAINMENT_EDGE_TYPES = new Set(['contains', 'containment', 'in-vpc', 'in-subnet']);

function manifestOf(n: PersistedNode): string {
  const p = (n.props ?? {}) as Record<string, unknown>;
  return (p._manifestId as string | undefined) ?? n.type;
}

function isKind(manifestId: string, keywords: readonly string[]): boolean {
  const m = manifestId.toLowerCase();
  return keywords.some((k) => m.includes(k));
}

function accountOf(n: PersistedNode): string | undefined {
  const p = (n.props ?? {}) as Record<string, unknown>;
  const acc =
    (p.accountId as string | undefined) ??
    ((p.tags as Record<string, string> | undefined)?.AccountId as string | undefined) ??
    undefined;
  return acc ? String(acc) : undefined;
}

function prettyTypeLabel(manifestId: string): string {
  const stripped = manifestId.startsWith('aws_') ? manifestId.slice(4) : manifestId;
  return stripped
    .split(/[-_]/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

function buildTree(
  nodes: readonly PersistedNode[],
  edges: readonly GraphEdgeLike[],
  t: TFn,
): TreeNode[] {
  // 1. Classify nodes and index.
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const accounts = new Map<string, PersistedNode>();
  const vpcs = new Map<string, PersistedNode>();
  const subnets = new Map<string, PersistedNode>();
  const resources: PersistedNode[] = [];

  for (const n of nodes) {
    const m = manifestOf(n);
    if (!m || m.startsWith('_')) continue;
    if (isKind(m, ['account'])) accounts.set(n.id, n);
    else if (isKind(m, ['vpc']) && !isKind(m, ['endpoint', 'peering'])) vpcs.set(n.id, n);
    else if (isKind(m, ['subnet'])) subnets.set(n.id, n);
    else resources.push(n);
  }

  // 2. Parent lookup from containment edges.
  const parentById = new Map<string, string>();
  for (const e of edges) {
    if (!CONTAINMENT_EDGE_TYPES.has(e.type)) continue;
    // Edges may be `container → contained` (`contains`, `in-vpc`) —
    // normalise so we always record parent→child.
    // For `in-vpc` and `in-subnet` the semantic is "child in parent";
    // `from` is the child in Compass's convention.
    if (e.type === 'in-vpc' || e.type === 'in-subnet') {
      parentById.set(e.from, e.to);
    } else {
      parentById.set(e.to, e.from);
    }
  }

  const findContainerOfType = (
    id: string,
    test: (manifestId: string) => boolean,
    hops = 6,
  ): string | undefined => {
    let cur: string | undefined = id;
    for (let i = 0; i < hops; i++) {
      cur = parentById.get(cur as string);
      if (!cur) return undefined;
      const n = byId.get(cur);
      if (n && test(manifestOf(n))) return cur;
    }
    return undefined;
  };

  // 3. Group resources by VPC or account-wide.
  const resourcesByVpc = new Map<string, PersistedNode[]>();
  const resourcesBySubnet = new Map<string, PersistedNode[]>();
  const accountWide = new Map<string, PersistedNode[]>(); // accountId → resources
  const DEFAULT_ACCOUNT = '__default__';

  for (const res of resources) {
    const subnetId = findContainerOfType(res.id, (m) => isKind(m, ['subnet']));
    const vpcId = findContainerOfType(res.id, (m) => isKind(m, ['vpc']) && !isKind(m, ['endpoint', 'peering']));
    if (subnetId) {
      const arr = resourcesBySubnet.get(subnetId) ?? [];
      arr.push(res);
      resourcesBySubnet.set(subnetId, arr);
    } else if (vpcId) {
      const arr = resourcesByVpc.get(vpcId) ?? [];
      arr.push(res);
      resourcesByVpc.set(vpcId, arr);
    } else {
      const accId = accountOf(res) ?? DEFAULT_ACCOUNT;
      const arr = accountWide.get(accId) ?? [];
      arr.push(res);
      accountWide.set(accId, arr);
    }
  }

  // 4. Group subnets under VPC; VPCs under account.
  const subnetsByVpc = new Map<string, PersistedNode[]>();
  for (const subnet of subnets.values()) {
    const vpcId = findContainerOfType(subnet.id, (m) => isKind(m, ['vpc']) && !isKind(m, ['endpoint', 'peering']));
    if (!vpcId) continue;
    const arr = subnetsByVpc.get(vpcId) ?? [];
    arr.push(subnet);
    subnetsByVpc.set(vpcId, arr);
  }

  const vpcsByAccount = new Map<string, PersistedNode[]>();
  for (const vpc of vpcs.values()) {
    const accId =
      findContainerOfType(vpc.id, (m) => isKind(m, ['account'])) ??
      accountOf(vpc) ??
      DEFAULT_ACCOUNT;
    const arr = vpcsByAccount.get(accId) ?? [];
    arr.push(vpc);
    vpcsByAccount.set(accId, arr);
  }

  // 5. Assemble. Build per-account trees. If we have no explicit
  //    account nodes, synthesise a single default root.
  const accountIds = new Set<string>([
    ...accounts.keys(),
    ...vpcsByAccount.keys(),
    ...accountWide.keys(),
  ]);

  const trees: TreeNode[] = [];
  for (const accId of accountIds) {
    const accNode = accounts.get(accId);
    const accLabel =
      accNode?.label != null
        ? String(accNode.label)
        : accId === DEFAULT_ACCOUNT
          ? t('compass.panel.resources.account')
          : accId;

    const vpcChildren: TreeNode[] = (vpcsByAccount.get(accId) ?? [])
      .sort((a, b) => String(a.label ?? a.id).localeCompare(String(b.label ?? b.id)))
      .map((vpc) => vpcToTree(vpc, subnetsByVpc, resourcesBySubnet, resourcesByVpc, t));

    const accountWideList = accountWide.get(accId) ?? [];
    const accountWideTree = buildAccountWideTree(accountWideList, t);

    const children: TreeNode[] = [];
    if (vpcChildren.length > 0) children.push(...vpcChildren);
    if (accountWideTree) children.push(accountWideTree);

    const count = sumCounts(children);
    trees.push({
      id: accNode?.id ?? `account:${accId}`,
      label: accLabel,
      kind: 'account',
      nodeRef: accNode,
      children,
      descendantResourceCount: count,
    });
  }

  // If a single synthesised account root has only one group, hoist its
  // children for a more compact tree.
  if (trees.length === 1 && trees[0].id === `account:${DEFAULT_ACCOUNT}`) {
    return trees[0].children;
  }

  return trees.sort((a, b) => a.label.localeCompare(b.label));
}

function vpcToTree(
  vpc: PersistedNode,
  subnetsByVpc: Map<string, PersistedNode[]>,
  resourcesBySubnet: Map<string, PersistedNode[]>,
  resourcesByVpc: Map<string, PersistedNode[]>,
  t: TFn,
): TreeNode {
  const subnetChildren: TreeNode[] = (subnetsByVpc.get(vpc.id) ?? [])
    .sort((a, b) => String(a.label ?? a.id).localeCompare(String(b.label ?? b.id)))
    .map((subnet) => {
      const grouped = groupByType(resourcesBySubnet.get(subnet.id) ?? [], t);
      const count = sumCounts(grouped);
      return {
        id: subnet.id,
        label: String(subnet.label ?? subnet.id),
        kind: 'subnet' as const,
        nodeRef: subnet,
        children: grouped,
        descendantResourceCount: count,
      };
    });

  // Resources that sit in this VPC but weren't tied to a specific
  // subnet (e.g. RDS instances attached via subnet group, ALBs at VPC
  // level) go under an inline "VPC-level" group.
  const vpcLevelRes = resourcesByVpc.get(vpc.id) ?? [];
  const vpcLevelChildren =
    vpcLevelRes.length > 0
      ? [
          {
            id: `${vpc.id}:__vpc-level`,
            label: t('compass.panel.resources.vpcLevel'),
            kind: 'type-group' as const,
            children: groupByType(vpcLevelRes, t),
            descendantResourceCount: vpcLevelRes.length,
          },
        ]
      : [];

  const children = [...subnetChildren, ...vpcLevelChildren];
  return {
    id: vpc.id,
    label: String(vpc.label ?? vpc.id),
    kind: 'vpc',
    nodeRef: vpc,
    children,
    descendantResourceCount: sumCounts(children),
  };
}

function buildAccountWideTree(resources: PersistedNode[], t: TFn): TreeNode | null {
  if (resources.length === 0) return null;
  const grouped = groupByType(resources, t);
  return {
    id: `__account-wide:${resources[0]?.id ?? 'misc'}`,
    label: t('compass.panel.resources.accountWide'),
    kind: 'account-wide',
    children: grouped,
    descendantResourceCount: resources.length,
  };
}

function groupByType(resources: PersistedNode[], t: TFn): TreeNode[] {
  const buckets = new Map<string, PersistedNode[]>();
  for (const r of resources) {
    const m = manifestOf(r);
    const arr = buckets.get(m) ?? [];
    arr.push(r);
    buckets.set(m, arr);
  }
  return Array.from(buckets.entries())
    .map(([manifestId, list]) => ({
      id: `type:${manifestId}:${list[0]?.id ?? ''}`,
      label: t('compass.panel.resources.typeGroupLabel', { type: prettyTypeLabel(manifestId), count: list.length }),
      kind: 'type-group' as const,
      children: list
        .sort((a, b) => String(a.label ?? a.id).localeCompare(String(b.label ?? b.id)))
        .map<TreeNode>((node) => ({
          id: node.id,
          label: String(node.label ?? node.id),
          kind: 'resource',
          nodeRef: node,
          children: [],
          descendantResourceCount: 1,
        })),
      descendantResourceCount: list.length,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function sumCounts(children: readonly TreeNode[]): number {
  let total = 0;
  for (const c of children) total += c.descendantResourceCount;
  return total;
}

function countResources(nodes: readonly TreeNode[]): number {
  let total = 0;
  for (const n of nodes) {
    if (n.kind === 'resource') total += 1;
    else total += countResources(n.children);
  }
  return total;
}

function filterTree(nodes: readonly TreeNode[], q: string): TreeNode[] {
  const out: TreeNode[] = [];
  for (const n of nodes) {
    if (n.kind === 'resource') {
      if (matchesQuery(n, q)) out.push(n);
      continue;
    }
    const childHits = filterTree(n.children, q);
    const selfHit = n.label.toLowerCase().includes(q);
    if (childHits.length > 0 || selfHit) {
      out.push({
        ...n,
        children: childHits.length > 0 ? childHits : n.children,
        descendantResourceCount:
          childHits.length > 0 ? countResources(childHits) : n.descendantResourceCount,
      });
    }
  }
  return out;
}

function matchesQuery(n: TreeNode, q: string): boolean {
  if (n.label.toLowerCase().includes(q)) return true;
  const ref = n.nodeRef;
  if (!ref) return false;
  if (ref.id.toLowerCase().includes(q)) return true;
  const tags = ((ref.props as { tags?: Record<string, string> } | undefined)?.tags ?? {}) as Record<
    string,
    string
  >;
  for (const v of Object.values(tags)) {
    if (String(v).toLowerCase().includes(q)) return true;
  }
  return false;
}
